import { Dispatch, MouseEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';

import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { IKnmKind } from 'APIPackage/DictionariesModule/interfaces/IKnmKind';
import { ApproveRequiredTypesEnum } from 'APIPackage/KnmModule/enums/ApproveRequiredTypesEnum';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import {
  getIsPlanErknmStatusConsidered,
  getIsPlanErknmStatusUnderConsideration,
} from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import {
  getDictionariesKnoOrganizationsSelectOptions,
  getKnoSupervisionStartAvailableUnplannedActivities,
} from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { KNM_INFO } from 'CorePackage/MainModule/routes/routes';
import { getApproveRequiredSelectOptionsByCreated } from 'KnmErknmPackage/MainModule/constants/approveRequiredSelectOptions';
import {
  APPROVE_REQUIRED_BLOCK_ID,
  DATA_CONTENT_BLOCK_ID,
  COLLABORATING_ORGANIZATIONS_BLOCK_ID,
  DIRECT_DURATION_DAYS_BLOCK_ID,
  DURATION_DAYS_BLOCK_ID,
  DURATION_HOURS_BLOCK_ID,
  getCollaboratingOrganizationBlockId,
  KIND_CONTROL_BLOCK_ID,
  KIND_KNM_BLOCK_ID,
  KNO_ORGANIZATION_BLOCK_ID,
  PLAN_ID_BLOCK_ID,
  TYPE_BLOCK_ID,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmDataErknmApproveAppeal,
  getKnmDataErknmApproveApproved,
  getKnmErknmKindsSelectOptions,
  getKnmErknmSupervisionTypesSelectOptions,
  getKnmFormRef,
  getKnmIsErknmStatusApproving,
  getKnmIsErknmStatusCommentsExist,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusReadyForApprove,
  getKnmIsErknmStatusRejectAppealed,
  getKnmIsErknmStatusRejected,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeCompleted,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsInitialData,
  getKnmIsNew,
  getKnmPlannedActivities,
  getKnmStartDateIsCurrentYear,
  getKnmIsWatchOnly,
  getCatalogKnmTypes248SelectOptions,
  getKnmDataErknmApproveRemarks,
  getKnmWatchOnlyByTestUser,
  getKnmDataMassDelete,
  getKnmDataMassCancel,
  getKnmIsNeedActSaveOrSign,
  getKnmOldKnoOrganization,
  getKnmDateReasonChangeKno,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import {
  getPlanListApprovedForCurrentYear,
  getPlanListPlanErknmStatus,
} from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { ApprovedTypesEnum } from 'APIPackage/KnmModule/enums/ApprovedTypesEnum';
import { saveFile } from 'CorePackage/MainModule/common/saveFile';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { knmInfoGetPlannedActivities, knmPlanFindGet } from 'KnmPackage/MainModule/actions/knmActions';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';

import { IKnmSignatureInfo } from '../../../APIPackage/KnmModule/interfaces/IKnmSignatureInfo';
import { ErknmPlanIdField } from './ErknmPlanIdField';
import { ErknmReasons } from '../../ErknmReasonsModule/components/ErknmReasons';
import { ErknmReasonsAttachments } from '../../ErknmReasonsModule/components/ErknmReasonsAttachments';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { composeValidators } from '../../../CorePackage/MainModule/validators/composeValidators';
import { makeIsNormalInteger } from '../../../CorePackage/MainModule/validators/isNormalInteger';
import { makeMaxNumber } from '../../../CorePackage/MainModule/validators/maxNumber';
import { makeIsNormalFloat } from '../../../CorePackage/MainModule/validators/isNormalFloat';
import { IDictionaryKnoOrganizationSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { ErknmSuspensionEvents } from '../../ErknmSuspensionEventsModule/ErknmSuspensionEvents';
import { IKnmErknmFormEvent } from '../../MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormSuspensionEvent } from '../../MainModule/interfaces/IKnmErknmFormSuspensionEvent';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { ISelectOption } from '../../../UIPackage/MainModule/components/SelectInput';
import { newPlanSelectOption } from '../../MainModule/constants/newPlanSelectOption';
import { createPlanSelectOption } from '../../MainModule/common/createPlanSelectOption';
import { planListSetPlan } from '../../../PlanListPackage/MainModule/actions/planListActions';
import { scrollToBlock } from '../../../CorePackage/MainModule/common/scrollToBlock';
import { KNM_NOTIFICATION_TITLE_BLOCK_ID } from '../../../KnmPackage/MainModule/constants/fieldsElementsIds';
import { TitleSectionElements } from './TitleSectionElements';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { IKnmAbrogationForm } from '../../../APIPackage/KnmModule/interfaces/IKnmAbrogationForm';
import { ErknmRescheduling } from './ErknmRescheduling';
import { getUserRoleIsSupervisor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { ErknmInfoDates } from './ErknmInfoDates';
import { ErknmCollaboratingOrganizations } from '../../../KnmPackage/MainModule/components/ErknmCollaboratingOrganizations';
import { ErknmFieldMark } from '../../MainModule/components/ErknmFieldMark';
import { ErknmAbrogation } from './ErknmAbrogation/ErknmAbrogation';
import { ErknmExcerpt } from './ErknmExcerpt/ErknmExcerpt';
import { ErknmApproveRemark } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemark';
import { ErknmApproveRemarkSectionCodeEnum } from '../../ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';
import { ErknmApproveRemarkAddButton } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemarkAddButton';
import { ErknmApproveRemarkChanges } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemarkChanges';
import { ErknmApproveRemarkFieldCodeEnum } from '../../ErknmApproveRemarkModule/enums/ErknmApproveRemarkFieldCodeEnum';
import { IDictionaryKnmKindSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryKnmKindSelectOption';
import { useIsKindKnmEnabled } from '../../MainModule/fieldsAvailability/erknmInfoAvailability';
import { useHasFieldApproveRemark } from '../../MainModule/common/useHasFieldApproveRemark';
import { isCollaboratingOrganizationsEnabled } from '../../MainModule/fieldsAvailability/erknmCollaboratingOrganizationsFieldsAvailability';
import { MassDeleteInfo } from '../../../KnmPackage/MainModule/components/MassDeleteInfo';
import { IKnmMassDelete } from '../../../APIPackage/KnmModule/interfaces/IKnmMassDelete';
import { MassCancelInfo } from '../../../KnmPackage/MainModule/components/MassCancelInfo';
import { IKnmMassCancel } from '../../../APIPackage/KnmModule/interfaces/IKnmMassCancel';
import styles from './ErknmInfo.module.scss';
import { Hint } from '../../../CorePackage/MainModule/components/Hint';
import { useKnoNameChangeText } from '../../../KnmPackage/MainModule/hooks/useKnmNameChangeHintText';

interface IErknmInfoProps {
  abrogation: IKnmAbrogationForm | null | undefined;
  accessToken?: string | null;
  approveRequired: ApproveRequiredTypesEnum | undefined;
  created: string | undefined;
  decisionDisabled: boolean | undefined;
  directDurationDays: string | undefined;
  disabledDecisionMessage: string | undefined;
  durationDays: string | undefined;
  durationHours: string | undefined;
  erpId?: IKnm['erpId'];
  eventsErknm?: IKnmErknmFormEvent[];
  isHasCollaboratingOrganization: boolean | undefined;
  isDurationDaysDisabled: boolean | undefined;
  isReasonsNotEmpty: boolean;
  kindControl: IDictionarySupervisionTypeSelectOption | undefined;
  kindKnmCode: KnmKindCodesEnum | undefined;
  knmId: number | undefined;
  knoOrganizationErknm: IDictionaryKnoOrganizationSelectOption | undefined;
  planGuid: IKnmErknmForm['planGuid'];
  planIdFromData: number | undefined;
  publicPortalURL: string;
  signatureInfo: IKnmSignatureInfo[] | null | undefined;
  signed: boolean | null | undefined;
  suspensionEvents?: IKnmErknmFormSuspensionEvent[];
  typeCode: IKnmTypeSelectOption['code'] | undefined;
  watchOnlyExtended: boolean;
  kindKnm: IDictionaryKnmKindSelectOption | undefined;
}

interface IErknmInfoStoreProps {
  availableUnplannedActivities: boolean | null | undefined;
  formRef: IKnmFormRef | undefined;
  isInitialData?: boolean;
  isKnmStatusApproving: boolean;
  isKnmStatusCommentsExist: boolean;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusReadyForApprove: boolean;
  isKnmStatusRejectAppealed: boolean;
  isKnmStatusRejected: boolean;
  isKnmStatusTypeCarriedOut: boolean;
  isKnmStatusTypeCompleted: boolean;
  isKnmStatusTypeWaitingCarryOut: boolean;
  isNewKnm: boolean;
  isStartDateIsCurrentYear: boolean;
  isSupervisor: boolean;
  isWatchOnly: boolean;
  kindsSelectOptions: IDictionarySelectOption<IKnmKind>[] | undefined;
  knmDataApproveAppeal: boolean | null | undefined;
  knmDataApproved: ApprovedTypesEnum | null | undefined;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
  knmTypes248SelectOptions: IKnmTypeSelectOption[];
  knoOrganizationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  plannedActivities: boolean | undefined;
  plansForSelectInput: number[];
  settings: IAdminSetting[] | undefined;
  supervisionTypesSelectOptions: IDictionarySupervisionTypeSelectOption[] | undefined;
  isWatchOnlyByTestUser: boolean;
  massCancelData: IKnmMassCancel | null | undefined;
  massDeleteData: IKnmMassDelete | null | undefined;
  isNeedActSaveOrSign?: boolean;
  dateReasonChangeKno: string | null | undefined;
  oldKnoOrganization: string | null | undefined;
}

const ErknmInfoWithoutStore = ({
  abrogation,
  accessToken,
  approveRequired,
  created,
  decisionDisabled,
  directDurationDays,
  disabledDecisionMessage,
  durationDays,
  durationHours,
  erpId,
  eventsErknm,
  isHasCollaboratingOrganization,
  isDurationDaysDisabled,
  isReasonsNotEmpty,
  kindControl,
  kindKnmCode,
  knmId,
  knoOrganizationErknm,
  planGuid,
  planIdFromData,
  publicPortalURL,
  signatureInfo,
  signed,
  suspensionEvents,
  typeCode,
  watchOnlyExtended,
  availableUnplannedActivities,
  formRef,
  isInitialData,
  isKnmStatusApproving,
  isKnmStatusCommentsExist,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
  isKnmStatusRejectAppealed,
  isKnmStatusRejected,
  isKnmStatusTypeCarriedOut,
  isKnmStatusTypeCompleted,
  isKnmStatusTypeWaitingCarryOut,
  isNewKnm,
  isStartDateIsCurrentYear,
  isSupervisor,
  isWatchOnly,
  kindsSelectOptions,
  knmDataApproveAppeal,
  knmDataApproved,
  knmPlanStatus,
  knmTypes248SelectOptions,
  knoOrganizationsSelectOptions,
  plannedActivities,
  plansForSelectInput,
  settings,
  supervisionTypesSelectOptions,
  kindKnm,
  isWatchOnlyByTestUser,
  massCancelData,
  massDeleteData,
  isNeedActSaveOrSign = false,
  dateReasonChangeKno,
  oldKnoOrganization,
}: IErknmInfoProps & IErknmInfoStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const remarks = useSelector(getKnmDataErknmApproveRemarks);
  const isDatesRemarksExists = useHasFieldApproveRemark(ErknmApproveRemarkSectionCodeEnum.DATES);
  const isEnabledCollaboratingOrganizations = isCollaboratingOrganizationsEnabled();

  const isTypeCodeVP = typeCode === KnmTypeCodesEnum.VP;
  const isTypeCodePP = typeCode === KnmTypeCodesEnum.PP;
  const isTypeCodePP248P294 = typeCode === KnmTypeCodesEnum.PP248P294;

  const isPlanType = typeCode ? isTypeCodePP : false;

  const knoNameChangeText = useKnoNameChangeText(knoOrganizationErknm?.label, oldKnoOrganization, dateReasonChangeKno);

  useEffect(() => {
    if (knoOrganizationErknm && kindControl) {
      dispatch(knmInfoGetPlannedActivities(knoOrganizationErknm.value.dictVersionId, kindControl.value.dictVersionId));
    }
  }, [dispatch, knoOrganizationErknm, kindControl]);

  const knmTypesTransformFunc = (items: IKnmTypeSelectOption[]) => {
    if (!knoOrganizationErknm || !kindControl) return [];

    return plannedActivities || !items ? items : items.filter(item => item.code !== KnmTypeCodesEnum.PP);
  };

  const isDisabledApproveRequiredSetting = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.LABEL_OF_APPROVE_REQUIRED_FIELD_4_1_2_STARTS_FROM,
  );

  const isKnmPlanStatusConsidered: boolean = getIsPlanErknmStatusConsidered(knmPlanStatus);
  const isKnmPlanStatusUnderConsideration: boolean = getIsPlanErknmStatusUnderConsideration(knmPlanStatus);
  const isKnmKindRemarkExist = useHasFieldApproveRemark(ErknmApproveRemarkSectionCodeEnum.KIND_KNM);

  const qrRef = useRef<HTMLDivElement>(null);
  const isDisableInitialFields = isInitialData || (!isNewKnm ? !!planIdFromData : false);

  const isDisableFieldsInPlanUnderConsideration =
    (isKnmStatusApproving || isKnmStatusCommentsExist || isKnmStatusTypeWaitingCarryOut || isKnmStatusRejected) &&
    isKnmPlanStatusUnderConsideration;

  const isDisableInfoField =
    isSupervisor &&
    (isKnmStatusApproving ||
      isKnmStatusRejectAppealed ||
      isKnmStatusTypeWaitingCarryOut ||
      isKnmStatusTypeCarriedOut ||
      isKnmStatusTypeCompleted);
  const isDisabledForSupervisorByCommentsExistStatus = isSupervisor && isKnmStatusCommentsExist;
  const isEnableApproveRequired = isSupervisor && (isKnmStatusFillInProcess || isKnmStatusReadyForApprove);

  const isShowSuspensionEvents =
    (isTypeCodeVP || isTypeCodePP) &&
    (kindKnmCode === KnmKindCodesEnum.MZ ||
      kindKnmCode === KnmKindCodesEnum.VP ||
      kindKnmCode === KnmKindCodesEnum.RO ||
      kindKnmCode === KnmKindCodesEnum.VK ||
      kindKnmCode === KnmKindCodesEnum.DP);

  const typeErknm = formRef?.current.getFieldState('typeErknm')?.value;

  const handleTypeSelectChange = useCallback(() => {
    const isOldTypeErknm = formRef?.current.getFieldState('typeErknm')?.value?.code === KnmTypeCodesEnum.PP248P294;

    formRef?.current.change('approved');
    formRef?.current.change('planId');
    formRef?.current.change('planGuid');
    formRef?.current.change('isCreatePlan');
    formRef?.current.change('planSelectOption');
    if (kindsSelectOptions && isOldTypeErknm) {
      formRef?.current.change('kindKnm');
    }
    formRef?.current.change('reasonsErknm');
    formRef?.current.change('reasonDocuments');
    formRef?.current.change('approveRequired');
  }, [formRef, kindsSelectOptions]);

  useEffect(() => {
    formRef?.current.change('oldPlanNumber');
    formRef?.current.change('oldKnmNumber');
  }, [formRef, isTypeCodePP248P294]);

  const kindOptions =
    kindsSelectOptions && isTypeCodePP248P294
      ? kindsSelectOptions.filter(
          kind =>
            kind.code === KnmKindCodesEnum.DP || kind.code === KnmKindCodesEnum.VP || kind.code === KnmKindCodesEnum.IV,
        )
      : kindsSelectOptions;

  const isActiveReasonFields =
    isSupervisor &&
    (isNewKnm ||
      isKnmStatusFillInProcess ||
      isKnmStatusReadyForApprove ||
      (isKnmStatusCommentsExist && isKnmPlanStatusConsidered) ||
      (knmDataApproved === ApprovedTypesEnum.APPROVED &&
        !!knmDataApproveAppeal &&
        knmPlanStatus === ErknmPlanStatusesEnum.CONSIDERED));

  const isActiveDurationDays =
    isSupervisor &&
    (isNewKnm ||
      isKnmStatusFillInProcess ||
      isKnmStatusReadyForApprove ||
      (isKnmStatusCommentsExist && isKnmPlanStatusConsidered && !remarks?.length) ||
      (isKnmStatusCommentsExist && isDatesRemarksExists) ||
      (knmDataApproved === ApprovedTypesEnum.APPROVED &&
        !!knmDataApproveAppeal &&
        knmPlanStatus === ErknmPlanStatusesEnum.CONSIDERED));

  const isUnscheduledType = typeCode ? isTypeCodeVP || isTypeCodePP : false;

  const isDisableTypeErknm =
    watchOnlyExtended ||
    isDisableInfoField ||
    isDisabledForSupervisorByCommentsExistStatus ||
    (!isKnmStatusFillInProcess && isDisableInitialFields) ||
    isDisableFieldsInPlanUnderConsideration;

  useEffect(() => {
    if (isNewKnm && !isDisableTypeErknm) {
      formRef?.current.change('typeErknm');
      formRef?.current.change('organizations', []);
    }
  }, [formRef, kindControl, isNewKnm, isDisableTypeErknm]);

  useEffect(() => {
    if (!isUnscheduledType) {
      formRef?.current.change('reasonsErknm');
      formRef?.current.change('reasonDocuments');
    }
  }, [formRef, isPlanType, isUnscheduledType]);

  const isActiveDirectDuration =
    isSupervisor &&
    (isNewKnm ||
      isKnmStatusFillInProcess ||
      isKnmStatusReadyForApprove ||
      (isKnmStatusCommentsExist && isKnmPlanStatusConsidered && !remarks?.length) ||
      (isKnmStatusCommentsExist && isDatesRemarksExists) ||
      (isKnmStatusTypeWaitingCarryOut &&
        knmDataApproved === ApprovedTypesEnum.APPROVED &&
        !!knmDataApproveAppeal &&
        knmPlanStatus === ErknmPlanStatusesEnum.CONSIDERED));
  const isActiveDirectDurationDays = isActiveDirectDuration && durationHours === undefined;
  const isActiveDurationHours = isActiveDirectDuration && directDurationDays === undefined;

  /**
   * Функция очистки формы отмены мероприятия при изменении причины отмены
   */
  const handleApproveRequiredSelectChange = useCallback(() => {
    formRef?.current.change('dataContent');
    formRef?.current.change('abrogation.reasonFile' as keyof IKnmForm);
    formRef?.current.change('abrogation.comment' as keyof IKnmForm);
    formRef?.current.change('abrogation.link' as keyof IKnmForm);
  }, [formRef]);

  const handleDownloadButton = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const canvas = qrRef.current?.lastChild as HTMLCanvasElement;
      const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');

      saveFile(pngUrl, `${erpId ?? 'qr'}.png`);
    },
    [erpId],
  );

  const handleKindKnmChange = useCallback(
    (e: IDictionarySelectOption<IKnmKind>) => {
      if (isKnmPlanStatusConsidered && isKnmKindRemarkExist) {
        return;
      }

      const objects = formRef?.current.getState().values.objectsErknm;

      if (e?.code === KnmKindCodesEnum.RO && objects && objects.length > 1) {
        formRef?.current.change('objectsErknm', [objects[0]]);
      }
      formRef?.current.change('organizations', []);
    },
    [formRef, isKnmPlanStatusConsidered, isKnmKindRemarkExist],
  );

  const handleDurationDaysClear = useCallback(() => {
    formRef?.current.change('durationDays');
    formRef?.current.change('isDurationDaysDisabled', false);
  }, [formRef]);

  const planSelectOptions: ISelectOption[] = [
    newPlanSelectOption,
    ...plansForSelectInput.map(item => createPlanSelectOption(item)),
  ];

  const handlePlanSelectOptionChange = useCallback(
    (e: ISelectOption | undefined) => {
      if (e?.value && e.value !== newPlanSelectOption.value) {
        formRef?.current.change('planId', e.value);
        formRef?.current.change('isCreatePlan', false);
        dispatch(knmPlanFindGet(e.value));
      } else if (e?.value === newPlanSelectOption.value) {
        formRef?.current.change('planId');
        formRef?.current.change('isCreatePlan', true);
        dispatch(planListSetPlan(null));
      } else {
        formRef?.current.change('planId');
        formRef?.current.change('isCreatePlan');
      }
    },
    [formRef, dispatch],
  );

  /**
   * Необходимость показа чекбокса "Не уведомлять контролируемое лицо"
   */
  const isShowCheckboxNotNotifySubject = useMemo(() => {
    return (
      availableUnplannedActivities === true && kindKnmCode === KnmKindCodesEnum.VP && typeCode === KnmTypeCodesEnum.VP
    );
  }, [availableUnplannedActivities]);

  const isShow22 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_22_STARTS_FROM);

  return (
    <div className={styles.InfoBody} id={KNM_INFO}>
      <div className={styles.TitleContainer}>
        <h2 className="shared-title__with-margin">Общая информация</h2>
        {publicPortalURL && accessToken && (
          <TitleSectionElements
            isSigned={signed}
            signatureInfo={signatureInfo}
            publicPortalURL={publicPortalURL}
            qrRef={qrRef}
            accessToken={accessToken}
            handleDownloadButton={handleDownloadButton}
          />
        )}
      </div>
      <Row>
        <FieldBlock label="Наименование органа контроля" id={KNO_ORGANIZATION_BLOCK_ID}>
          <SelectInputField
            id="knoOrganizationErknm"
            name="knoOrganizationErknm"
            options={knoOrganizationsSelectOptions}
            isClearable
            isSearchable
            disabled={true}
            size="medium"
            validate={makeRequired('Поле не может быть пустым')}
          />
          {knoNameChangeText && <Hint margin="top" text={knoNameChangeText} />}
        </FieldBlock>
      </Row>
      <Row>
        <FieldBlock label="Вид контроля (надзора) и его номер" id={KIND_CONTROL_BLOCK_ID}>
          <SelectInputField
            id="kindControl"
            name="kindControl"
            options={supervisionTypesSelectOptions}
            isSearchable
            disabled={true}
            size="medium"
          />
        </FieldBlock>
      </Row>
      <Row>
        <FieldBlock
          label={
            <div className="shared-inline-flex">
              Вид КНМ
              <ErknmApproveRemarkChanges
                sectionCode={ErknmApproveRemarkSectionCodeEnum.KIND_KNM}
                fieldCode={ErknmApproveRemarkFieldCodeEnum.KIND_KNM}
                oldValue={kindKnm?.title}
              />
            </div>
          }
          id={KIND_KNM_BLOCK_ID}
          button={<ErknmApproveRemarkAddButton sectionCode={ErknmApproveRemarkSectionCodeEnum.KIND_KNM} />}
        >
          <ErknmApproveRemark sectionCode={ErknmApproveRemarkSectionCodeEnum.KIND_KNM} />
          <FieldBlock size="half">
            <SelectInputField
              id="kindKnm"
              name="kindKnm"
              options={kindOptions}
              disabled={!useIsKindKnmEnabled()}
              size="medium"
              onChange={handleKindKnmChange}
            />
          </FieldBlock>
        </FieldBlock>
      </Row>
      <Row>
        <FieldBlock size="half" label="Характер КНМ" id={TYPE_BLOCK_ID}>
          <SelectInputField
            id="typeErknm"
            name="typeErknm"
            disabled={true}
            size="medium"
            onChange={handleTypeSelectChange}
            options={knmTypesTransformFunc(knmTypes248SelectOptions)}
            transformFunc={knmTypesTransformFunc}
          />
        </FieldBlock>
        {isPlanType && (
          <FieldBlock size="half" label="Номер плана" id={PLAN_ID_BLOCK_ID}>
            {isStartDateIsCurrentYear ? (
              <SelectInputField
                id="planSelectOption"
                name="planSelectOption"
                options={planSelectOptions}
                size="medium"
                onChange={handlePlanSelectOptionChange}
                disabled={watchOnlyExtended || (!isKnmStatusFillInProcess && isDisableInitialFields)}
              />
            ) : (
              <ErknmPlanIdField
                watchOnly={watchOnlyExtended}
                disabled={!isKnmStatusFillInProcess}
                planGuid={planGuid}
              />
            )}
          </FieldBlock>
        )}
      </Row>
      {isTypeCodePP248P294 && (
        <Row>
          <FieldBlock size="half" label="Номер плана 294-ФЗ" id="oldPlanNumber">
            <TextInputField name="oldPlanNumber" disabled={watchOnlyExtended} />
          </FieldBlock>
          <FieldBlock size="half" label="Номер проверки 294-ФЗ" id="oldKnmNumber">
            <TextInputField name="oldKnmNumber" disabled={watchOnlyExtended} />
          </FieldBlock>
        </Row>
      )}

      <ErknmCollaboratingOrganizations
        created={created}
        watchOnly={!isEnabledCollaboratingOrganizations || isNeedActSaveOrSign}
        organizationsBlockId={COLLABORATING_ORGANIZATIONS_BLOCK_ID}
        fieldsLabel={
          <>
            Наименование органа контроля, с которым КНМ проводится совместно
            <ErknmFieldMark fieldName="collaboratingOrganizationsErknm" />
          </>
        }
        isHasCollaboratingOrganization={isHasCollaboratingOrganization}
        getOrganizationsItemBlockId={getCollaboratingOrganizationBlockId}
      />

      <ErknmExcerpt
        created={created}
        watchOnlyExtended={watchOnlyExtended}
        typeCode={typeCode}
        kindKnmCode={kindKnmCode}
        knmId={knmId}
        decisionDisabled={decisionDisabled}
        disabledDecisionMessage={disabledDecisionMessage}
      />

      <ErknmInfoDates
        isApproveRequired={approveRequired === ApproveRequiredTypesEnum.REQUIRED}
        typeCode={typeCode}
        isNewKnm={isNewKnm}
        formRef={formRef}
        isWatchOnly={isWatchOnly || isWatchOnlyByTestUser}
      />
      <Row>
        <FieldBlock
          size="half"
          label={getTitleFieldWithRedStar({
            label: `Срок непосредственного взаимодействия (дней)`,
            field: `directDurationDays`,
            isPm: false,
            isDecision: false,
            isKnm: true,
            additionContent: (
              <ErknmApproveRemarkChanges
                sectionCode={ErknmApproveRemarkSectionCodeEnum.DATES}
                fieldCode={ErknmApproveRemarkFieldCodeEnum.DIRECT_DURATION_DAYS}
                oldValue={directDurationDays}
              />
            ),
          })}
          color="black"
          id={DIRECT_DURATION_DAYS_BLOCK_ID}
          placementLabel="left"
        >
          <TextInputField
            className="shared-row-field"
            name="directDurationDays"
            disabled={isWatchOnly || !isActiveDirectDurationDays}
          />
        </FieldBlock>
        <FieldBlock
          size="half"
          label={getTitleFieldWithRedStar({
            label: `Срок непосредственного взаимодействия (часов)`,
            field: `durationHours`,
            isPm: false,
            isDecision: false,
            isKnm: true,
            additionContent: (
              <ErknmApproveRemarkChanges
                sectionCode={ErknmApproveRemarkSectionCodeEnum.DATES}
                fieldCode={ErknmApproveRemarkFieldCodeEnum.DURATION_HOURS}
                oldValue={durationHours}
              />
            ),
          })}
          color="black"
          id={DURATION_HOURS_BLOCK_ID}
          placementLabel="left"
        >
          <TextInputField
            className="shared-row-field"
            name="durationHours"
            disabled={isWatchOnly || !isActiveDurationHours}
            validate={composeValidators(
              makeIsNormalFloat('Введены недопустимые символы'),
              makeMaxNumber(256, 'Число не может превышать 256'),
            )}
          />
        </FieldBlock>
      </Row>
      <Row>
        <FieldBlock
          size="half"
          label={
            <>
              Срок проведения (дней)
              <ErknmFieldMark fieldName="durationDays" />
            </>
          }
          color="black"
          id={DURATION_DAYS_BLOCK_ID}
          placementLabel="left"
        >
          <TextInputField
            className="shared-row-field"
            name="durationDays"
            disabled={isWatchOnly || !isActiveDurationDays || isNewKnm || isDurationDaysDisabled}
            clearable={isActiveDurationDays && !isNewKnm && !!durationDays}
            onClear={handleDurationDaysClear}
            isCanBeChangedInNonWorkDaysMessage={isActiveDurationDays && !isNewKnm && isDurationDaysDisabled}
            validate={composeValidators(
              makeIsNormalInteger('Введены недопустимые символы'),
              makeMaxNumber(365, 'Число не может превышать 365'),
            )}
          />
        </FieldBlock>
      </Row>

      <MassCancelInfo massCancel={massCancelData} />
      <MassDeleteInfo massDelete={massDeleteData} />

      {typeErknm?.code === KnmTypeCodesEnum.PP && <ErknmRescheduling watchOnly={isWatchOnly} />}

      {isShow22 && !massCancelData?.cancelled && <ErknmAbrogation abrogation={abrogation} />}

      {isShowSuspensionEvents && (
        <ErknmSuspensionEvents
          eventsErknm={eventsErknm}
          kindKnmCode={kindKnmCode}
          suspensionEvents={suspensionEvents}
          created={created}
        />
      )}
      {isUnscheduledType && <ErknmReasons watchOnly={isWatchOnly || !isActiveReasonFields} typeCode={typeCode} />}
      {(approveRequired === ApproveRequiredTypesEnum.PROMPTLY ||
        approveRequired === ApproveRequiredTypesEnum.REQUIRED) &&
        isTypeCodeVP && (
          <ErknmReasonsAttachments
            watchOnly={watchOnlyExtended || isDisableInfoField || isDisableFieldsInPlanUnderConsideration}
            isUnscheduledType={isUnscheduledType}
            typeCode={typeCode}
          />
        )}
      {isReasonsNotEmpty && isUnscheduledType && !isTypeCodePP && (
        <Row>
          <FieldBlock label="Необходимость согласования" size="half" id={APPROVE_REQUIRED_BLOCK_ID}>
            <SelectInputField
              id="approveRequired"
              name="approveRequired"
              options={getApproveRequiredSelectOptionsByCreated(created, settings)}
              disabled={isDisabledApproveRequiredSetting || !isEnableApproveRequired}
              size="medium"
              onChange={handleApproveRequiredSelectChange}
              validate={makeRequired('Поле не может быть пустым')}
            />
          </FieldBlock>
        </Row>
      )}
      {approveRequired === ApproveRequiredTypesEnum.PROMPTLY && (
        <Row>
          <FieldBlock
            id={DATA_CONTENT_BLOCK_ID}
            label={
              <>
                Сведения о причинении вреда (ущерба) или об угрозе причинения вреда (ущерба) охраняемым законом
                ценностям в случаях отсутствия необходимости согласования проведения контрольного (надзорного)
                мероприятия с органами прокуратуры в соответствии со статьей 66 Федерального закона
                <ErknmFieldMark fieldName="dataContent" />
              </>
            }
            className="shared-field"
          >
            <TextareaField
              id="dataContent"
              name="dataContent"
              disabled={
                watchOnlyExtended ||
                isDisableInfoField ||
                isDisabledForSupervisorByCommentsExistStatus ||
                isDisableFieldsInPlanUnderConsideration
              }
            />
          </FieldBlock>
        </Row>
      )}

      {isShowCheckboxNotNotifySubject && (
        <Button
          className="shared-btn-add"
          text="Возможность не уведомлять контролируемое лицо"
          variant="link"
          color="primary"
          type="button"
          onClick={() => scrollToBlock(KNM_NOTIFICATION_TITLE_BLOCK_ID)}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState): IErknmInfoStoreProps => ({
  formRef: getKnmFormRef(state),
  isWatchOnly: getKnmIsWatchOnly(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  knmPlanStatus: getPlanListPlanErknmStatus(state),
  knoOrganizationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
  supervisionTypesSelectOptions: getKnmErknmSupervisionTypesSelectOptions(state),
  kindsSelectOptions: getKnmErknmKindsSelectOptions(state),
  isInitialData: getKnmIsInitialData(state),
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isKnmStatusReadyForApprove: getKnmIsErknmStatusReadyForApprove(state),
  isKnmStatusApproving: getKnmIsErknmStatusApproving(state),
  isKnmStatusRejected: getKnmIsErknmStatusRejected(state),
  isKnmStatusCommentsExist: getKnmIsErknmStatusCommentsExist(state),
  isKnmStatusRejectAppealed: getKnmIsErknmStatusRejectAppealed(state),
  isKnmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  isKnmStatusTypeCompleted: getKnmIsErknmStatusTypeCompleted(state),
  knmDataApproved: getKnmDataErknmApproveApproved(state),
  knmDataApproveAppeal: getKnmDataErknmApproveAppeal(state),
  isNewKnm: getKnmIsNew(state),
  plannedActivities: getKnmPlannedActivities(state),
  settings: getAdminFetchSettings(state),
  plansForSelectInput: getPlanListApprovedForCurrentYear(state),
  isStartDateIsCurrentYear: getKnmStartDateIsCurrentYear(state),
  availableUnplannedActivities: getKnoSupervisionStartAvailableUnplannedActivities(state),
  knmTypes248SelectOptions: getCatalogKnmTypes248SelectOptions(state),
  isWatchOnlyByTestUser: getKnmWatchOnlyByTestUser(state),
  massCancelData: getKnmDataMassCancel(state),
  massDeleteData: getKnmDataMassDelete(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
  dateReasonChangeKno: getKnmDateReasonChangeKno(state),
  oldKnoOrganization: getKnmOldKnoOrganization(state),
});

export const ErknmInfo = connect(mapStateToProps)(ErknmInfoWithoutStore);
